/* MCR AI-style predictive search: typewriter placeholder, trending/recent/AI chips, debounced predictive
   results (sections/predictive-search.liquid), smart intent & spelling chips, keyboard navigation,
   skeleton loading, AbortController, mobile full-screen sheet. No dependencies. */
(function () {
  'use strict';
  if (customElements.get('mcr-search')) return;

  var doc = document;
  var root = doc.documentElement;
  var RECENT_KEY = 'mcr:search:recent';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var sheetMq = window.matchMedia('(max-width: 989px)');

  function lines(t) {
    return String(t || '')
      .split(/\r?\n|<br\s*\/?>/i)
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function reEsc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function norm(s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); }

  // Levenshtein distance with an early exit once the distance exceeds `max`.
  function lev(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [];
    var i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      var cur = [i];
      var rowMin = i;
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }

  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  }
  function writeRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
  }

  var CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>';
  var SPARK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M12 3c.6 4.2 2.8 6.4 7 7-4.2.6-6.4 2.8-7 7-.6-4.2-2.8-6.4-7-7 4.2-.6 6.4-2.8 7-7Z"/></svg>';
  var CROSS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7 7 17"/></svg>';

  class McrSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var cfgEl = this.querySelector('[data-mcr-search-config]');
      try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { this.cfg = {}; }
      this.cfg.strings = this.cfg.strings || {};
      this.predictive = this.getAttribute('data-predictive') === 'true';

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-mcr-search-panel]');
      this.idle = this.querySelector('[data-mcr-search-idle]');
      this.live = this.querySelector('[data-mcr-search-live]');
      this.results = this.querySelector('[data-mcr-results]');
      this.intentBox = this.querySelector('[data-mcr-intent]');
      this.intentList = this.querySelector('[data-mcr-intent-list]');
      this.notice = this.querySelector('[data-mcr-notice]');
      this.statusEl = this.querySelector('[data-mcr-status]');
      this.clearBtn = this.querySelector('button[type="reset"]');
      this.typed = this.querySelector('[data-mcr-typed]');
      this.recentWrap = this.querySelector('[data-mcr-recent]');
      this.recentList = this.querySelector('[data-mcr-recent-list]');
      this.headerEl = this.closest('sticky-header');

      this.keywords = lines(this.cfg.keywords);
      this.intents = lines(this.cfg.intents)
        .map(function (l) {
          var p = l.split('=>');
          return p.length > 1 && p[0].trim() && p[1].trim() ? [norm(p[0]), p[1].trim()] : null;
        })
        .filter(Boolean);
      var vocabCache = null;
      Object.defineProperty(this, 'vocab', { configurable: true, get: function () { return vocabCache || (vocabCache = this.buildVocab()); } });

      this.cache = new Map();
      this.controller = null;
      this.timer = 0;
      this.active = -1;
      this.isOpen = false;
      this.query = '';
      this.corrections = [];

      this.bind();
      this.syncValueState();
      // Non-critical work waits until the browser is idle so it never competes with first paint / LCP.
      var self = this;
      var later = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1200); };
      later(function () { self.renderRecent(); self.startTyping(); }, { timeout: 3000 });
    }

    /* ---------- setup ---------- */
    bind() {
      var self = this;
      this.input.addEventListener('focus', function () { self.open(); self.pauseTyping(true); });
      this.input.addEventListener('blur', function () { self.pauseTyping(false); });
      this.input.addEventListener('input', function () { self.onInput(); });
      this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
      this.form.addEventListener('reset', function () {
        setTimeout(function () { self.onInput(); self.input.focus(); });
      });
      this.addEventListener('click', function (e) { self.onClick(e); });
      this.addEventListener('focusout', function (e) {
        if (self.classList.contains('is-sheet')) return;
        var to = e.relatedTarget;
        if (to && !self.contains(to)) self.close();
      });
      doc.addEventListener('pointerdown', function (e) {
        if (!self.isOpen || self.contains(e.target) || e.target.closest('[data-mcr-search-open]')) return;
        if (self.classList.contains('is-sheet')) return;
        self.close();
      });
      doc.addEventListener('click', function (e) {
        var opener = e.target.closest && e.target.closest('[data-mcr-search-open]');
        if (opener && opener.getAttribute('aria-controls') === self.id) {
          e.preventDefault();
          self.open(true);
          self.input.focus({ preventScroll: true });
        }
      });
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) {
          self.close();
          if (self.contains(doc.activeElement)) self.input.blur();
        }
      });
      window.addEventListener('resize', function () {
        if (!self.isOpen) return;
        if (!sheetMq.matches && self.classList.contains('is-sheet')) self.setSheet(false);
        self.position();
      }, { passive: true });
    }

    buildVocab() {
      var set = new Set();
      var add = function (text) {
        norm(text).split(/[^a-z]+/).forEach(function (w) { if (w.length >= 4) set.add(w); });
      };
      this.intents.forEach(function (p) { add(p[1]); });
      this.keywords.forEach(add);
      this.querySelectorAll('[data-mcr-query]').forEach(function (el) { add(el.getAttribute('data-mcr-query')); });
      return set;
    }

    /* ---------- typewriter placeholder ---------- */
    startTyping() {
      // Starts with the static label (e.g. "Search for dhotis, shirts, sets..."), erases it, then cycles keywords.
      if (!this.typed || !this.keywords.length || reduceMotion.matches) return;
      var self = this;
      var ph = this.querySelector('[data-mcr-ph]');
      var speed = Math.max(30, parseInt(this.cfg.speed, 10) || 70);
      var k = -1;
      var word = this.typed.textContent;
      var i = word.length;
      var deleting = true;
      var visible = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) self.kick && self.kick();
        }).observe(this);
      }
      var tick = function () {
        self.tt = 0;
        if (self.paused || !visible || doc.hidden) return; // resumes via kick()
        if (k > -1) word = self.keywords[k];
        if (!deleting) {
          i++;
          self.typed.textContent = word.slice(0, i);
          if (i >= word.length) {
            deleting = true;
            self.tt = setTimeout(tick, 1800);
            return;
          }
          self.tt = setTimeout(tick, speed + Math.random() * speed * 0.6);
        } else {
          i--;
          self.typed.textContent = word.slice(0, Math.max(0, i));
          if (i <= 0) {
            deleting = false;
            if (k === -1 && ph) ph.classList.remove('is-static');
            k = (k + 1) % self.keywords.length;
            self.tt = setTimeout(tick, 380);
            return;
          }
          self.tt = setTimeout(tick, speed * 0.45);
        }
      };
      this.kick = function () { if (!self.tt && !self.paused) self.tt = setTimeout(tick, 400); };
      doc.addEventListener('visibilitychange', function () { if (!doc.hidden) self.kick(); });
      // start after the page has fully loaded so the animation never competes with the first paint
      var begin = function () { if (!self.tt) self.tt = setTimeout(tick, 2500); };
      if (doc.readyState === 'complete') begin(); else window.addEventListener('load', begin, { once: true });
    }

    pauseTyping(on) {
      this.paused = on || !!this.input.value;
      if (!this.paused && this.kick) this.kick();
    }

    /* ---------- open / close ---------- */
    open(fromIcon) {
      if (this.isOpen) return;
      this.isOpen = true;
      clearTimeout(this.hideTimer);
      this.panel.hidden = false;
      if (sheetMq.matches) this.setSheet(true);
      void this.panel.offsetWidth;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      if (this.headerEl) {
        this.headerEl.preventHide = true;
        this.headerEl.setRaised && this.headerEl.setRaised(true);
      }
      this.renderRecent();
      this.showState();
      this.position();
      if (fromIcon) this.pauseTyping(true);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.setActive(-1);
      if (this.classList.contains('is-sheet')) this.setSheet(false);
      var self = this;
      this.hideTimer = setTimeout(function () { if (!self.isOpen) self.panel.hidden = true; }, reduceMotion.matches ? 0 : 220);
      if (this.headerEl) {
        this.headerEl.preventHide = false;
        this.headerEl.setRaised && this.headerEl.setRaised(false);
      }
    }

    setSheet(on) {
      this.classList.toggle('is-sheet', on);
      if (on) root.classList.add('mcr-lock');
      else if (!doc.querySelector('.mcr-modal.is-open, mcr-drawer.is-open')) root.classList.remove('mcr-lock');
    }

    position() {
      if (sheetMq.matches) {
        this.panel.style.removeProperty('left');
        this.panel.style.removeProperty('width');
        return;
      }
      var bar = this.form.getBoundingClientRect();
      var vw = root.clientWidth;
      var want = parseFloat(getComputedStyle(this.panel).getPropertyValue('--mcr-panel-w')) || 880;
      var w = Math.min(want, vw - 32);
      var left = (bar.width - w) / 2;
      var abs = bar.left + left;
      if (abs < 16) left += 16 - abs;
      if (abs + w > vw - 16) left -= abs + w - (vw - 16);
      this.panel.style.width = w + 'px';
      this.panel.style.left = Math.round(left) + 'px';
      this.panel.style.setProperty('--mcr-panel-max', Math.max(280, window.innerHeight - bar.bottom - 24) + 'px');
    }

    showState() {
      var hasQ = !!this.input.value.trim();
      this.idle.hidden = hasQ;
      this.live.hidden = !hasQ;
      this.setActive(-1);
    }

    syncValueState() {
      var has = !!this.input.value;
      this.classList.toggle('has-value', has);
      if (this.clearBtn) this.clearBtn.hidden = !has;
    }

    /* ---------- input ---------- */
    onInput() {
      this.syncValueState();
      this.pauseTyping(doc.activeElement === this.input);
      var q = this.input.value.trim();
      if (!this.isOpen) this.open();
      clearTimeout(this.timer);
      this.showState();
      if (!q) {
        this.query = '';
        if (this.controller) this.controller.abort();
        this.removeAttribute('loading');
        return;
      }
      this.query = q;
      this.renderIntent(q);
      if (!this.predictive) {
        this.results.innerHTML = '';
        return;
      }
      if (!this.cache.has(norm(q))) this.showSkeleton();
      var self = this;
      this.timer = setTimeout(function () { self.fetchResults(q); }, 200);
    }

    onSubmit(e) {
      var q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
        this.input.focus();
        return;
      }
      this.saveRecent(q);
    }

    onClick(e) {
      var t = e.target;
      var close = t.closest('[data-mcr-search-close]');
      if (close) {
        e.preventDefault();
        this.close();
        this.input.blur();
        return;
      }
      var rm = t.closest('[data-mcr-recent-remove]');
      if (rm) {
        e.preventDefault();
        var v = rm.getAttribute('data-mcr-recent-remove');
        writeRecent(readRecent().filter(function (x) { return x !== v; }));
        this.renderRecent();
        this.input.focus();
        return;
      }
      if (t.closest('[data-mcr-recent-clear]')) {
        e.preventDefault();
        writeRecent([]);
        this.renderRecent();
        this.input.focus();
        return;
      }
      var chip = t.closest('[data-mcr-query]');
      if (chip && this.contains(chip)) {
        var q = chip.getAttribute('data-mcr-query');
        var href = chip.getAttribute('href') || '';
        var isSearchLink = !href || href.indexOf(this.cfg.searchUrl || '/search') === 0;
        if (chip.hasAttribute('data-mcr-fill') || (this.predictive && isSearchLink && !chip.closest('[data-mcr-results]'))) {
          e.preventDefault();
          this.input.value = q;
          this.input.focus();
          this.onInput();
          return;
        }
        this.saveRecent(q);
        return;
      }
      if (t.closest('[data-mcr-results] a')) this.saveRecent(this.input.value.trim());
    }

    onKeydown(e) {
      var key = e.key;
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        if (!this.isOpen) this.open();
        var opts = this.options();
        if (!opts.length) return;
        var i = this.active + (key === 'ArrowDown' ? 1 : -1);
        if (i < 0) i = opts.length - 1;
        if (i >= opts.length) i = 0;
        this.setActive(i, opts);
      } else if (key === 'Enter') {
        var el = this.activeEl;
        if (el && this.isOpen) {
          e.preventDefault();
          if (el.matches('button[type="submit"], .mcr-ps__cta')) {
            this.saveRecent(this.input.value.trim());
            this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
          } else {
            el.click();
          }
        }
      } else if (key === 'Escape') {
        if (this.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
      } else if (key === 'Tab' && this.classList.contains('is-sheet')) {
        // keep focus inside the full-screen sheet
        var f = Array.prototype.filter.call(this.querySelectorAll('a[href], button:not([hidden]), input:not([type="hidden"])'), function (x) { return x.offsetParent !== null; });
        if (f.length && e.shiftKey && doc.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      }
    }

    options() {
      var scope = this.live.hidden ? this.idle : this.live;
      return Array.prototype.filter.call(
        scope.querySelectorAll('a[data-mcr-option], [data-mcr-intent-list] button, [data-mcr-empty-chips] a, [data-mcr-results] li[role="option"] a, .mcr-ps__cta'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    setActive(i, opts) {
      if (this.activeEl) {
        this.activeEl.classList.remove('is-active');
        var li = this.activeEl.closest('[role="option"]');
        if (li) li.setAttribute('aria-selected', 'false');
        if (this.activeEl.getAttribute('role') === 'option') this.activeEl.setAttribute('aria-selected', 'false');
      }
      this.active = i;
      this.activeEl = null;
      if (i < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      opts = opts || this.options();
      var el = opts[i];
      if (!el) return;
      if (!el.id) el.id = this.id + '-opt-' + Math.random().toString(36).slice(2, 8);
      el.classList.add('is-active');
      var liEl = el.closest('[role="option"]');
      if (liEl) liEl.setAttribute('aria-selected', 'true');
      if (el.getAttribute('role') === 'option') el.setAttribute('aria-selected', 'true');
      this.activeEl = el;
      this.input.setAttribute('aria-activedescendant', (liEl && liEl.id) || el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    /* ---------- recent ---------- */
    saveRecent(q) {
      if (!this.cfg.enableRecent) return;
      q = String(q || '').trim();
      if (!q) return;
      var list = readRecent().filter(function (x) { return norm(x) !== norm(q); });
      list.unshift(q);
      writeRecent(list.slice(0, 6));
    }

    renderRecent() {
      if (!this.recentWrap || !this.cfg.enableRecent) return;
      var list = readRecent();
      this.recentWrap.hidden = !list.length;
      var base = this.cfg.searchUrl || '/search';
      var removeLabel = this.cfg.strings.remove || 'Remove';
      this.recentList.innerHTML = list
        .map(function (q) {
          return (
            '<li class="mcr-search__recent"><a class="mcr-chip" data-mcr-option data-mcr-query="' + esc(q) + '" href="' + base + '?q=' +
            encodeURIComponent(q) + '&amp;options%5Bprefix%5D=last">' + CLOCK + esc(q) + '</a>' +
            '<button type="button" class="mcr-search__rm" data-mcr-recent-remove="' + esc(q) + '" aria-label="' + esc(removeLabel) + ': ' + esc(q) + '">' + CROSS + '</button></li>'
          );
        })
        .join('');
    }

    /* ---------- smart intent / did you mean ---------- */
    suggest(q) {
      var ql = norm(q);
      var out = [];
      var add = function (s) {
        s = norm(s);
        if (s && s !== ql && out.indexOf(s) === -1) out.push(s);
      };
      var words = ql.split(' ');
      this.intents.forEach(function (pair) {
        var term = pair[0];
        var sugg = pair[1];
        if (term.indexOf(' ') > -1) {
          if (ql.indexOf(term) > -1) add(ql.replace(term, sugg));
          return;
        }
        words.forEach(function (w, i) {
          var limit = term.length > 7 ? 2 : 1;
          if (w === term || (term.length >= 5 && w.length >= 4 && lev(w, term, limit) <= limit)) {
            var nw = words.slice();
            nw[i] = sugg;
            add(nw.join(' '));
          }
        });
      });
      var vocab = this.vocab;
      var changed = false;
      var fixed = words.map(function (w) {
        if (w.length < 4 || vocab.has(w) || /\d/.test(w)) return w;
        var limit = w.length >= 7 ? 2 : 1;
        var best = null;
        var bestD = limit + 1;
        vocab.forEach(function (v) {
          if (Math.abs(v.length - w.length) > limit) return;
          var d = lev(w, v, limit);
          if (d < bestD) { bestD = d; best = v; }
        });
        if (best && bestD <= limit) { changed = true; return best; }
        return w;
      });
      if (changed) add(fixed.join(' '));
      return out.slice(0, 3);
    }

    renderIntent(q) {
      this.corrections = this.cfg.enableIntent ? this.suggest(q) : [];
      if (!this.intentBox) return;
      this.intentBox.hidden = !this.corrections.length;
      this.intentList.innerHTML = this.corrections
        .map(function (s) {
          return '<li><button type="button" class="mcr-chip mcr-chip--ai" data-mcr-fill data-mcr-query="' + esc(s) + '">' + SPARK + esc(s) + '</button></li>';
        })
        .join('');
    }

    /* ---------- results ---------- */
    showSkeleton() {
      var line = '<span class="mcr-sk mcr-sk--line"></span>';
      var card = '<div class="mcr-sk-card"><span class="mcr-sk mcr-sk--media"></span><span class="mcr-sk mcr-sk--line"></span><span class="mcr-sk mcr-sk--line mcr-sk--short"></span></div>';
      this.results.innerHTML =
        '<div class="mcr-ps__grid mcr-ps__grid--skeleton" aria-hidden="true"><div class="mcr-ps__side">' + line + line + line + line +
        '</div><div class="mcr-ps__main"><div class="mcr-ps__products">' + card + card + card + card + '</div></div></div>';
      this.setStatus(this.cfg.strings.loading);
    }

    buildUrl(q) {
      var limit = Math.min(10, Math.max(1, parseInt(this.cfg.limit, 10) || 6));
      return (
        (this.cfg.predictiveUrl || '/search/suggest') +
        '?q=' + encodeURIComponent(q) +
        '&section_id=predictive-search' +
        '&resources[type]=query,product,collection,page,article' +
        '&resources[limit]=' + limit +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,tag'
      );
    }

    fetchResults(q, correctedFrom) {
      var key = norm(q);
      if (this.cache.has(key)) {
        this.renderResults(this.cache.get(key), q, correctedFrom);
        return;
      }
      if (this.controller) this.controller.abort();
      var controller = (this.controller = new AbortController());
      this.setAttribute('loading', '');
      var deferred = this.searchEvent(q);
      var self = this;
      fetch(this.buildUrl(q), { signal: controller.signal, credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error(String(res.status));
          return res.text();
        })
        .then(function (text) {
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var box = parsed.querySelector('#predictive-search-results');
          var html = box ? box.innerHTML : '';
          self.cache.set(key, html);
          if (norm(self.query) !== norm(correctedFrom || q)) return; // stale response
          self.renderResults(html, q, correctedFrom);
          if (deferred) deferred.resolve({ totalCount: self.total || 0 });
        })
        .catch(function (err) {
          if (deferred) deferred.reject(err);
          if (err && err.name === 'AbortError') return;
          if (norm(self.query) === norm(correctedFrom || q)) self.renderEmpty(q);
        })
        .finally(function () {
          if (self.controller === controller) self.removeAttribute('loading');
        });
    }

    searchEvent(q) {
      try {
        var SE = window.StandardEvents && window.StandardEvents.SearchUpdateEvent;
        if (!SE) return null;
        var d = SE.createPromise();
        this.dispatchEvent(new SE({ search: { query: q }, promise: d.promise }));
        return d;
      } catch (e) {
        return null;
      }
    }

    renderResults(html, q, correctedFrom) {
      var s = this.cfg.strings;
      this.results.innerHTML = html;
      this.results.querySelectorAll('.predictive-search__loading-state').forEach(function (el) { el.remove(); });
      var headings = s.headings || {};
      this.results.querySelectorAll('[data-mcr-ps-heading]').forEach(function (h) {
        var t = headings[h.getAttribute('data-mcr-ps-heading')];
        if (t) h.textContent = t;
      });
      var cta = this.results.querySelector('[data-predictive-search-search-for-text]');
      var typed = this.input.value.trim();
      if (cta && s.searchFor) cta.textContent = s.searchFor + ' “' + typed + '”';
      var ctaBtn = this.results.querySelector('.mcr-ps__cta');
      if (ctaBtn) ctaBtn.setAttribute('type', 'submit');
      this.highlight(q);

      var countEl = this.results.querySelector('[data-total-results]');
      this.total = countEl ? parseInt(countEl.getAttribute('data-total-results'), 10) || 0 : 0;
      this.setActive(-1);

      if (!this.total) {
        if (!correctedFrom && this.corrections.length) {
          this.fetchResults(this.corrections[0], q);
          return;
        }
        this.renderEmpty(correctedFrom || q);
      }
      if (correctedFrom && this.total) {
        this.notice.hidden = false;
        this.notice.innerHTML = '<span class="mcr-ai-badge">' + SPARK + 'MCR</span> ' + esc(s.showing || '') + ' <strong>“' + esc(q) + '”</strong>';
      } else {
        this.notice.hidden = true;
      }
      this.setStatus(countEl ? countEl.textContent.trim() : '');
      if (this.total && this.classList.contains('mcr-search--ed')) this.fetchCount(q);
    }

    /* Live catalogue total for the editorial panel ("42 results"), via sections/mcr-search-counts.liquid */
    fetchCount(q) {
      var self = this;
      var key = 'n:' + norm(q);
      var paint = function (n) {
        if (norm(self.query) !== norm(q) && norm(self.input.value) !== norm(q)) return;
        var label = n + ' ' + (n === 1 ? (self.cfg.strings.result || 'result') : (self.cfg.strings.results || 'results'));
        var head = self.results.querySelector('.mcr-ps__main .mcr-ps__group');
        if (head && !head.querySelector('.mcr-ps__count')) {
          var el = doc.createElement('span');
          el.className = 'mcr-ps__count';
          head.insertBefore(el, head.firstChild);
        }
        var c = head && head.querySelector('.mcr-ps__count');
        if (c) c.textContent = label;
        var cta = self.results.querySelector('.mcr-ps__cta-wrap');
        if (cta) cta.setAttribute('data-count', label);
      };
      if (this.cache.has(key)) { paint(this.cache.get(key)); return; }
      fetch((this.cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last&section_id=mcr-search-counts', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          var m = /data-mcr-search-count="(d+)"/.exec(t);
          if (!m) return;
          var n = parseInt(m[1], 10);
          self.cache.set(key, n);
          paint(n);
        })
        .catch(function () {});
    }

    renderEmpty(q) {
      var tpl = this.querySelector('[data-mcr-empty-tpl]');
      if (!tpl) return;
      var node = tpl.content.firstElementChild.cloneNode(true);
      var s = this.cfg.strings;
      node.querySelector('[data-mcr-empty-heading]').textContent = (s.emptyHeading || '') + ' “' + q + '”';
      var chips = node.querySelector('[data-mcr-empty-chips]');
      var src = this.querySelectorAll('[data-mcr-trending] a');
      Array.prototype.slice.call(src, 0, 6).forEach(function (a) {
        var c = a.cloneNode(true);
        c.setAttribute('data-mcr-fill', '');
        chips.appendChild(c);
      });
      this.results.querySelectorAll('.mcr-search__empty').forEach(function (el) { el.remove(); });
      this.results.insertBefore(node, this.results.firstChild);
      this.setStatus(node.querySelector('[data-mcr-empty-heading]').textContent);
    }

    highlight(q) {
      var words = norm(q).split(' ').filter(function (w) { return w.length > 1; }).map(reEsc);
      if (!words.length) return;
      var re = new RegExp('(' + words.join('|') + ')', 'gi');
      this.results.querySelectorAll('[data-mcr-hl]').forEach(function (el) {
        var text = el.textContent;
        el.innerHTML = esc(text).replace(re, '<mark class="mcr-hl">$1</mark>');
      });
    }

    setStatus(text) {
      if (!this.statusEl) return;
      this.statusEl.textContent = '';
      var el = this.statusEl;
      setTimeout(function () { el.textContent = text || ''; }, 60);
    }
  }

  customElements.define('mcr-search', McrSearch);
})();
